import * as Sentry from '@sentry/react';
import { Button, Result } from 'antd';
import type React from 'react';
import { useCallback } from 'react';

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
  eventId?: string;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetError, eventId }) => {
  const handleReload = useCallback(() => {
    resetError();
    window.location.reload();
  }, [resetError]);

  const handleReportFeedback = useCallback(() => {
    if (eventId) {
      Sentry.showReportDialog({ eventId });
    }
  }, [eventId]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '20px',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Result
        status="error"
        title="Something went wrong"
        subTitle="An unexpected error occurred. The error has been reported to our team. You can try reloading the page."
        extra={[
          <Button type="primary" key="reload" onClick={handleReload}>
            Reload Page
          </Button>,
          eventId && (
            <Button key="feedback" onClick={handleReportFeedback}>
              Report Feedback
            </Button>
          ),
        ]}
      >
        {import.meta.env.DEV && (
          <div
            style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: '#fff',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              textAlign: 'left',
            }}
          >
            <h4>Error Details (Development Only):</h4>
            <pre
              style={{
                fontSize: '12px',
                overflow: 'auto',
                maxHeight: '300px',
                backgroundColor: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
              }}
            >
              {error.message}
              {'\n\n'}
              {error.stack}
            </pre>
            {eventId && (
              <p style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                Event ID: {eventId}
              </p>
            )}
          </div>
        )}
      </Result>
    </div>
  );
};

interface SentryErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  showDialog?: boolean;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

export const SentryErrorBoundary: React.FC<SentryErrorBoundaryProps> = ({
  children,
  fallback,
  showDialog = false,
  onError,
}) => {
  const handleError = useCallback(
    (error: unknown, componentStack: string, eventId: string) => {
      const errorObj = error instanceof Error ? error : new Error(String(error));

      if (import.meta.env.DEV) {
        console.error('ErrorBoundary caught an error:', errorObj);
        console.error('Component Stack:', componentStack);
        console.error('Sentry Event ID:', eventId);
      }

      if (onError) {
        onError(errorObj, { componentStack });
      }

      if (showDialog && eventId) {
        Sentry.showReportDialog({ eventId });
      }
    },
    [onError, showDialog]
  );

  return (
    <Sentry.ErrorBoundary
      fallback={(props) => {
        const FallbackComponent = fallback || ErrorFallback;
        const errorObj =
          props.error instanceof Error ? props.error : new Error(String(props.error));
        return <FallbackComponent {...props} error={errorObj} />;
      }}
      onError={handleError}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
